/**
 * Snapshot-based undo/redo.
 *
 * The document is small, plain and JSON-shaped, so whole-state snapshots are simpler
 * and far more robust than a command log — there is no way for an inverse operation to
 * drift out of sync with a forward one.
 *
 * Dragging a slider fires dozens of updates per second. Those are coalesced by key
 * within a time window, so one drag becomes one undo step rather than sixty.
 */
export interface HistoryEntry<T> {
  doc: T;
  label: string;
}

export interface History<T> {
  past: HistoryEntry<T>[];
  future: HistoryEntry<T>[];
  /** Key and timestamp of the most recent commit, used for coalescing. */
  lastKey: string | null;
  lastTime: number;
}

export const HISTORY_LIMIT = 80;
const COALESCE_MS = 700;

export function emptyHistory<T>(): History<T> {
  return { past: [], future: [], lastKey: null, lastTime: 0 };
}

/**
 * Records `previous` as an undo step before a change is applied.
 *
 * When `coalesceKey` repeats within the time window the step is folded into the
 * previous one — the older snapshot is the correct restore point for the whole drag.
 */
export function pushHistory<T>(
  history: History<T>,
  previous: T,
  label: string,
  coalesceKey?: string,
): History<T> {
  const now = Date.now();
  const coalesce =
    coalesceKey !== undefined && history.lastKey === coalesceKey && now - history.lastTime < COALESCE_MS;

  if (coalesce) {
    return { ...history, future: [], lastTime: now };
  }

  const past = [...history.past, { doc: previous, label }];
  if (past.length > HISTORY_LIMIT) past.shift();

  return { past, future: [], lastKey: coalesceKey ?? null, lastTime: now };
}

export interface UndoResult<T> {
  history: History<T>;
  doc: T;
  label: string;
}

export function undo<T>(history: History<T>, current: T): UndoResult<T> | null {
  const entry = history.past[history.past.length - 1];
  if (!entry) return null;
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [{ doc: current, label: entry.label }, ...history.future],
      lastKey: null,
      lastTime: 0,
    },
    doc: entry.doc,
    label: entry.label,
  };
}

export function redo<T>(history: History<T>, current: T): UndoResult<T> | null {
  const entry = history.future[0];
  if (!entry) return null;
  return {
    history: {
      past: [...history.past, { doc: current, label: entry.label }],
      future: history.future.slice(1),
      lastKey: null,
      lastTime: 0,
    },
    doc: entry.doc,
    label: entry.label,
  };
}
